import { useEffect, useRef } from 'react';

/**
 * A pancake that trails the mouse pointer. Decorative easter egg.
 *
 * Gated on `(hover: hover) and (pointer: fine)` rather than a viewport width:
 * that asks whether there is actually a mouse, so a narrow desktop window keeps
 * it and a large tablet does not. Also skipped entirely under
 * prefers-reduced-motion -- something chasing the cursor is exactly the kind of
 * unrequested movement that setting is for.
 *
 * Position is written straight to the node inside a rAF loop; putting pointer
 * coordinates into React state would re-render the tree on every mouse move.
 *
 * Note: the design system says "Emoji: never" -- the mascot is meant to be the
 * expressive element. This is a deliberate exception.
 */
export function PancakeCursor() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const hasMouse = window.matchMedia('(hover: hover) and (pointer: fine)');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!hasMouse.matches || reduced.matches) return;

    let targetX = 0;
    let targetY = 0;
    let x = 0;
    let y = 0;
    let seen = false;
    let raf = 0;

    const onMove = (event: PointerEvent) => {
      targetX = event.clientX;
      targetY = event.clientY;
      if (!seen) {
        // Jump to the first known position rather than gliding in from 0,0.
        seen = true;
        x = targetX;
        y = targetY;
        el.style.opacity = '1';
      }
    };

    const onLeave = () => {
      el.style.opacity = '0';
      seen = false;
    };

    const tick = () => {
      // Lerp toward the pointer so it trails rather than sticking to it.
      x += (targetX - x) * 0.14;
      y += (targetY - y) * 0.14;
      el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      raf = requestAnimationFrame(tick);
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerleave', onLeave);
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerleave', onLeave);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none fixed left-0 top-0 z-[100] select-none opacity-0 transition-opacity duration-300 will-change-transform"
    >
      {/* Offset down-right of the pointer instead of centred on it, so the
          cursor itself and whatever it is over stay unobscured. */}
      <span className="block translate-x-[14px] translate-y-[12px] text-[28px] leading-none">
        🥞
      </span>
    </div>
  );
}
